import React from 'react';
import PilihV39 from './PilihV39';
import { useT } from '../i18n';

/* ══ Control Panel — tab Agent AI (v3.9 sahaja) ═════════════════════════
   Panel kanan untuk tab Agent AI, adik-beradik panel Flow dan panel
   Schedule dalam App.jsx. Ia menggantikan DUA bar mendatar yang dahulu
   duduk di atas senarai kad (bar "Agent Saya" + bar "Scrape Agent AI"),
   yang muncul dan hilang mengikut kandungan tab — jadi kawalan berpindah
   tempat di depan mata pengguna.

   ⭐ SUIS "Smart" (arahan user 2026-08-14) yang menentukan isi panel:

     MATI (lalai) — agent yang SESAAT sudah siapkan. Buat masa ini
                    FLOWNITY sahaja, dan panel memang menyatakannya;
                    jangan janji module lain sebelum ia wujud.
     HIDUP        — agent custom GPT milik pelanggan sendiri, dibina di
                    PRO Studio → Agent Saya. Wajib Configure dahulu.

   Suis itu juga menentukan KAD MANA yang dijalankan (Smart mati = kad
   FLOWNITY, Smart hidup = kad Smart Custom), kerana dua enjin itu mengisi
   bentuk field yang berbeza. Sebab itu jalur kaki menyebut bilangan kad
   yang dilangkau — tanpa itu pengguna menanda 13 kad, 8 sahaja berjalan,
   dan tiada apa-apa di skrin yang menerangkan kenapa.

   ⚠️ TEKS PANEL INI kini MENGIKUT SUIS BAHASA (BM/EN). Dahulu ia dikunci
      English (arahan user 2026-08-14) kerana panel Flow dan Schedule ketika
      itu berbahasa Melayu, dan komen lama meramalkan "kalau panel lain
      ditukar, tukar sekali". Itu berlaku 2026-08-18 — semua panel kini
      dwibahasa, jadi panel ini disambung ke kamus (`ws.ap.*`). Panel ini
      v3.9 SAHAJA (nama komponen *V39, dirender hanya di bawah suis uiV39),
      jadi useT() dipanggil terus — tiada corak penampan diperlukan.

   ⚠️ Panel ini TIDAK memegang state. Semuanya milik WorkspaceView (senarai
      agent, agent dipilih, platform, kiraan kad, keadaan larian) dan
      sampai ke sini sebagai `data`; setiap tekanan dipulangkan melalui
      callback. Menyalin state ke sini bermakna dua sumber kebenaran untuk
      perkara yang sama, dan yang di sini akan basi senyap.

   ⛔ Warna terang inline DILARANG di sini — panel ini hidup dalam tema
      gelap v3.9. Guna token (--s-ink, --s-line, --s-fill). Lihat nota
      "Warna terang yang ditulis INLINE" dalam design/ui.css. */

/* Satu bacaan, dipakai oleh badan DAN jalur kaki. Kalau dikira dua kali,
   satu daripadanya akan terlepas bila peraturan berubah — dan yang paling
   mudah terlepas ialah kiraan pada butang Run, benda yang pengguna
   percaya sebelum menekan. */
function bacaAgentPanel(data, smart) {
    const d = data || {};
    const kiraan = d.counts || { flownity: 0, smart: 0, lain: 0 };
    const jumlah = (kiraan.flownity || 0) + (kiraan.smart || 0) + (kiraan.lain || 0);
    const agents = Array.isArray(d.agents) ? d.agents : [];
    const agent = agents.find((a) => a.id === d.agentId) || null;
    const layak = smart ? (kiraan.smart || 0) : (kiraan.flownity || 0);
    return {
        agents,
        agent,
        layak,
        dilangkau: Math.max(0, jumlah - layak),
        /* Smart hidup: agent WAJIB dipilih dan sudah Configure. Tanpa
           padanan field, jawapan agent tidak tahu hendak masuk ke medan
           mana pada kad — larian akan menghasilkan kad kosong. */
        sedia: smart ? !!(agent && agent.configured) : true,
        running: !!d.running,
        progress: d.progress || { current: 0, total: 0 },
        platform: d.platform === 'gemini' ? 'gemini' : 'chatgpt',
        platformNames: d.platformNames || { chatgpt: 'ChatGPT (GPTs)', gemini: 'Gemini (Gems)' },
        geminiLocked: !!d.geminiLocked,
    };
}

/* Nama platform = nama PRODUK milik orang lain (ChatGPT, Gemini), jadi ia
   DATA dan bukan ayat UI — tidak diterjemah, sama seperti nama module.
   ⚠️ Sengaja pendek: senarai FLOWNITY di atas memakai "ChatGPT (GPTs)" /
   "Gemini (Gems)" kerana di sana pelanggan memilih ANTARA dua tempat scrape.
   Di sini ia hanya menandakan agent itu tinggal di mana, dan nama panjang
   memanjangkan setiap baris dalam senarai tanpa memberi apa-apa. */
const NAMA_PLATFORM = { chatgpt: 'ChatGPT', gemini: 'Gemini' };
const namaPlatform = (p) => (p === 'gemini' ? NAMA_PLATFORM.gemini : NAMA_PLATFORM.chatgpt);

/* Agent yang MENYIMPAN GAMBAR ke card (suis "Simpan gambar" di PRO Studio, Langkah 2) —
   arahan user 2026-09-13. Pelanggan perlu tahu SEBELUM Run: setiap card memerlukan ruang
   gambar kosong sebanyak bilangan ini, dan larian ditahan kalau ada card yang penuh.
   WorkspaceView yang mengira (`imageTurnsDaripadaAgent`); panel hanya memapar. Nilai
   rosak / tiada (data panel lama) = 0 = tiada tanda. */
const bilGambarAgent = (x) => Math.max(0, Math.floor(Number(x?.imageTurns) || 0));

const barisStatus = (kunci, nilai) => (
    <div key={kunci} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '2.5px 0' }}>
        <span style={{ color: 'var(--s-ink-3)', flex: 'none' }}>{kunci}</span>
        <span style={{ color: 'var(--s-ink)', textAlign: 'right', minWidth: 0 }}>{nilai}</span>
    </div>
);

/* 'none' | 'all' | nombor babak — bentuk yang sama seperti PRO Studio.
   `t` dihantar masuk kerana ini fungsi biasa, bukan komponen. */
const bacaPilihanBabak = (t, v) => {
    if (v === 'all') return t('ws.ap.sceneEvery');
    if (v === 'none' || v === undefined || v === null) return t('ws.ap.sceneNone');
    const n = Number(v);
    return Number.isFinite(n) ? t('ws.ap.sceneN', { n }) : t('ws.ap.sceneNone');
};

export function AgentPanelBodyV39({ data, smart, onPilihAgent, onPilihPlatform, onBukaProStudio }) {
    const t = useT();
    const v = bacaAgentPanel(data, smart);

    /* Smart MATI — agent siap sedia. Tiada apa untuk dikonfigurasi di
       sini; platform sahaja, dan Gemini masih berkunci kerana automation
       Gem memang belum pernah dibina. */
    if (!smart) {
        return (
            <div className="s-sect" style={{ padding: 0, borderBottom: 'none' }}>
                <p className="s-sect__title">{t('ws.ap.title')}</p>
                <div>
                    <PilihV39
                        ariaLabel={t('ws.ap.title')}
                        nilai={v.platform}
                        disabled={v.running}
                        onPilih={(id) => onPilihPlatform && onPilihPlatform(id)}
                        pilihan={[
                            { id: 'chatgpt', label: v.platformNames.chatgpt },
                            {
                                id: 'gemini',
                                label: v.platformNames.gemini,
                                nota: v.geminiLocked ? t('ws.ap.comingSoon') : undefined,
                                disabled: v.geminiLocked,
                            },
                        ]}
                    />
                    <p className="s-hint">{t('ws.ap.readyMade')}</p>
                </div>
            </div>
        );
    }

    /* Smart HIDUP tetapi pelanggan langsung tiada agent. Ini pintu masuk
       PRO Studio, bukan jalan buntu — jadi ia membawa butang, bukan
       sekadar ayat memberitahu tiada apa-apa. */
    if (v.agents.length === 0) {
        return (
            <div className="s-sect" style={{ padding: 0, borderBottom: 'none' }}>
                <p className="s-sect__title">{t('ws.ap.myAgent')}</p>
                <div>
                    <span className="s-lbl" style={{ marginBottom: 0 }}>{t('ws.ap.noAgentYet')}</span>
                    <p className="s-hint">{t('ws.ap.buildOwn')}</p>
                    <button
                        type="button"
                        className="s-btn s-btn--ghost s-btn--sm"
                        style={{ marginTop: 11 }}
                        /* Tiada agent dipilih di sini — panggil TANPA argumen
                           supaya objek event klik tidak disalah anggap sebagai
                           id agent (yang akan menghasilkan URL rosak). */
                        onClick={() => onBukaProStudio()}
                    >
                        {t('ws.ap.openProStudio')}
                    </button>
                </div>
            </div>
        );
    }

    const a = v.agent;
    const babak = Number(a?.sceneCount) || 1;
    const gambarSahaja = a?.output === 'image';
    /* Bilangan field yang benar-benar berpadan dengan jawapan agent.
       Ini ukuran yang bermakna — bukan bilangan field dalam borang, yang
       kekal sama walaupun tiada satu pun berpadan. */
    const fieldPadan = Number(a?.fieldsMatched) || 0;

    return (
        <div className="s-sect" style={{ padding: 0, borderBottom: 'none' }}>
            <p className="s-sect__title">{t('ws.ap.myAgent')}</p>
            <div>
                <PilihV39
                    ariaLabel={t('ws.ap.myAgent')}
                    nilai={a ? a.id : ''}
                    kosong={t('ws.ap.selectAgent')}
                    disabled={v.running}
                    onPilih={(id) => onPilihAgent && onPilihAgent(id)}
                    /* Nota baris = platform, kemudian tanda simpan gambar, kemudian status.
                       Platform didahulukan kerana itu yang pelanggan cari semasa MEMILIH
                       (agent ChatGPT dan Gemini duduk bercampur dalam satu senarai). Simpan
                       gambar ialah sifat agent seperti platform, jadi ia menyusul terus;
                       status hanya penting selepas dia memilih. */
                    pilihan={v.agents.map((x) => {
                        const bilGambar = bilGambarAgent(x);
                        return {
                            id: x.id,
                            label: x.name,
                            nota: [
                                namaPlatform(x.platform),
                                bilGambar > 0 ? t(bilGambar > 1 ? 'ws.ap.savesImagesMany' : 'ws.ap.savesImagesOne', { n: bilGambar }) : null,
                                x.configured ? null : t('ws.ap.notConfigured'),
                            ].filter(Boolean).join(' · '),
                        };
                    })}
                />

                {!a && <p className="s-hint">{t('ws.ap.pickToSetup')}</p>}

                {a && !a.configured && (
                    <>
                        <div
                            style={{
                                marginTop: 11, borderRadius: 10, padding: '10px 11px',
                                background: 'var(--s-warn-soft)', color: 'var(--s-warn)',
                                display: 'flex', alignItems: 'flex-start', gap: 8,
                                fontSize: 12.5, lineHeight: 1.45,
                            }}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: 16, flex: 'none', marginTop: 1 }}>warning</span>
                            <span>{t('ws.ap.notConfiguredLong')}</span>
                        </div>
                        <button
                            type="button"
                            className="s-btn s-btn--ghost s-btn--sm"
                            style={{ marginTop: 11 }}
                            onClick={() => onBukaProStudio(a.id)}
                        >
                            {t('ws.ap.openProStudio')}
                        </button>
                    </>
                )}

                {a && a.configured && (
                    <>
                        <div style={{ marginTop: 11, borderRadius: 10, padding: '11px 12px', background: 'var(--s-fill)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--s-ok)', marginBottom: 7 }}>
                                <span className="material-symbols-outlined" style={{ fontSize: 16, flex: 'none' }}>check_circle</span>
                                <span>{t('ws.ap.configured')}{a.configuredAt ? `, ${a.configuredAt}` : ''}</span>
                            </div>
                            {barisStatus(t('ws.ap.platform'), namaPlatform(a.platform))}
                            {/* Butang pemilih hanya memapar NAMA agent — nota "Simpan N gambar" hilang
                                sebaik agent dipilih. Baris ini mengekalkannya kelihatan. Agent yang tidak
                                menyimpan gambar tiada baris ini (bukan "Tidak"), sama seperti tanda di senarai. */}
                            {bilGambarAgent(a) > 0 && barisStatus(
                                t('ws.ap.saveImages'),
                                t(bilGambarAgent(a) > 1 ? 'ws.ap.perCardMany' : 'ws.ap.perCardOne', { n: bilGambarAgent(a) }),
                            )}
                            {barisStatus(t('ws.ap.setsPerProduct'), Number(a.setCount) || 1)}
                            {barisStatus(t('ws.ap.scenes'), babak)}
                            {barisStatus(t('ws.ap.output'), gambarSahaja
                                ? t(babak > 1 ? 'ws.ap.outImgNoVidMany' : 'ws.ap.outImgNoVidOne', { n: babak })
                                : t(babak > 1 ? 'ws.ap.outImgVidMany' : 'ws.ap.outImgVidOne', { n: babak }))}
                            {barisStatus(t('ws.ap.productPhoto'), bacaPilihanBabak(t, a.productPhoto))}
                            {barisStatus(t('ws.ap.avatar'), bacaPilihanBabak(t, a.avatar))}
                            {barisStatus(t('ws.ap.fieldsMatched'), fieldPadan)}
                        </div>
                        <button
                            type="button"
                            className="s-btn s-btn--quiet s-btn--sm"
                            style={{ marginTop: 9, paddingLeft: 0 }}
                            onClick={() => onBukaProStudio(a.id)}
                        >
                            {t('ws.ap.editInProStudio')}
                            <span className="material-symbols-outlined" style={{ fontSize: 15, marginLeft: 4 }}>arrow_forward</span>
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export function AgentPanelFootV39({ data, smart, onRun }) {
    const t = useT();
    const v = bacaAgentPanel(data, smart);
    const boleh = !v.running && v.layak > 0 && v.sedia;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--s-ink-3)' }}>
                    {v.running
                        ? t('ws.ap.running', { c: v.progress.current || 0, t: v.progress.total || 0 })
                        : v.layak === 0 ? t('ws.ap.noProduct') : t('ws.ap.nSelected', { n: v.layak })}
                </span>
                {/* Baris ini muncul HANYA bila ada kad yang benar-benar akan
                    ditinggalkan. Bila tiada, panel kekal bersih. */}
                {!v.running && v.dilangkau > 0 && (
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--s-ink-3)', marginTop: 2 }}>
                        {t('ws.ap.skipped', { n: v.dilangkau, jenis: smart ? t('ws.ap.notSmart') : t('ws.ap.notFlownity') })}
                    </span>
                )}
                {/* Kad yang sedang diproses. Ini BUKAN tambahan baharu — bar
                    lama memaparkan baris yang sama (`chatgptProgress.label`),
                    dan tanpanya larian panjang kelihatan tergantung. */}
                {v.running && v.progress.label && (
                    <span
                        title={v.progress.label}
                        style={{
                            display: 'block', fontSize: 11, color: 'var(--s-ink-3)', marginTop: 2,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}
                    >
                        {v.progress.label}
                    </span>
                )}
            </span>
            {/* Bentuk butang SAMA seperti Run Flow dan Run TikTok — tiga
                panel ini adik-beradik, jadi butangnya tidak boleh nampak
                datang dari app lain. */}
            <button
                onClick={onRun}
                disabled={!boleh}
                title={v.running ? t('ws.ap.tipRunning')
                    : !v.sedia ? t('ws.ap.tipPickConfigured')
                        : v.layak === 0 ? t('ws.ap.tipSelectCard')
                            : (smart ? t('ws.ap.runMyAgent') : t('ws.ap.runAgentAI'))}
                style={{
                    flex: 1, height: 40, background: '#6366f1',
                    color: '#ffffff', borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 12,
                    cursor: boleh ? 'pointer' : 'not-allowed',
                    opacity: boleh ? 1 : 0.45,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: boleh ? '0 4px 12px rgba(99,102,241,0.3)' : 'none', transition: 'all 0.2s',
                    textTransform: 'uppercase', letterSpacing: '0.02em',
                }}
            >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>play_arrow</span>
                {smart ? t('ws.ap.runMyAgent') : t('ws.ap.runAgentAI')}
            </button>
        </div>
    );
}
